/**
 * MemberButton — medlemsknappen fra «Hvem er du?» i Stell_Kjokken: avatar
 * (84 px) og kortnavn på et glasskort, 132 px bred. Delt av avatarrutenettet
 * i ProfileLogin og raden i hvilemodus (#162), så de ser like ut.
 *
 * Personfargene er lyse pasteller i begge temaer (#151); InitialAvatar har
 * mørk tekst på dem. Navnet står også synlig, så fargen er aldri eneste
 * kjennetegn.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { RosterMember } from '@stell/shared';
import { InitialAvatar, asColorSlot } from '../../components/primitives/InitialAvatar';

export function memberDisplayName(m: Pick<RosterMember, 'name' | 'short_name'>): string {
  return m.short_name || m.name;
}

export interface MemberButtonProps {
  member: RosterMember;
  onClick: () => void;
  testId: string;
}

export function MemberButton({ member, onClick, testId }: MemberButtonProps): ReactElement {
  const { t } = useTranslation();
  const name = memberDisplayName(member);
  return (
    <button
      data-testid={testId}
      type="button"
      onClick={onClick}
      aria-label={t('login.member_aria', { name: member.name })}
      className="bg-card border-card-line text-ink flex w-33 flex-col items-center gap-1.5 rounded-xl border px-2.5 py-3.5"
    >
      <InitialAvatar
        name={name}
        colorSlot={asColorSlot(member.color_token)}
        size={84}
        decorative
        className="mb-1"
      />
      <span className="text-lg font-extrabold">{name}</span>
    </button>
  );
}
